import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Question, WarningCircle } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { Glyph } from './glyphs';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';
import { SkipLink, MAIN_ID } from './SkipLink';
import { ThemeToggle } from './ThemeToggle';
import { routes } from '../../../routes/routes';
import './shells.css';
import { Wordmark } from '../../brand';

/** Desktop draws Phosphor, mobile draws its own 24 grid SVG. Never crossed. */
const TONE_ICONS = {
  success: { desktop: CheckCircle, mobile: 'check' },
  error: { desktop: WarningCircle, mobile: 'warning' },
  neutral: { desktop: Question, mobile: 'question' },
};

/**
 * StatusShell. No app chrome, one centred column.
 *
 * Payment success, payment failed, registration complete, email verification,
 * and 404. Five routes, one fixed anatomy:
 *
 *   tone-lifted disc holding one glyph
 *   optional kicker (the status code, and it is a KICKER, not a headline)
 *   headline
 *   one supporting line, 44ch
 *   optional detail block (receipt, reason, next steps) via `children`
 *   one primary command and at most one ghost escape hatch
 *
 * TONE RULE. The DISC carries the outcome: success tint for a good result,
 * error tint for a failure, neutral for a dead end. The BUTTON never does. It
 * stays ink, because ink is command and a button is a command regardless of how
 * the user arrived here. A green button would be using colour to mean two
 * things at once.
 *
 * WHAT BRANCHES ON `useViewport()` AND WHY
 * ----------------------------------------
 * Only the class prefix and the icon source. The DOM is identical at both
 * sizes, because a centred column is a centred column, but the frame must not
 * hand a page a `dsk-` container to put `ds-` controls in, and the mobile track
 * draws inline SVG where the desktop track draws Phosphor. Nothing structural
 * branches, and nothing is rendered twice.
 *
 * @param {{
 *   children?: React.ReactNode,
 *   tone?: 'success'|'error'|'neutral',
 *   icon?: object,
 *   kicker?: string,
 *   title?: string,
 *   description?: React.ReactNode,
 *   actions?: React.ReactNode,
 *   showWordmark?: boolean,
 *   showThemeToggle?: boolean,
 *   className?: string
 * }} props
 */
export function StatusShell({
  children,
  tone = 'neutral',
  icon,
  kicker,
  title,
  description,
  actions,
  showWordmark = true,
  showThemeToggle = true,
  className,
}) {
  const { isDesktop } = useViewport();
  const prefix = isDesktop ? 'dsk' : 'ds';
  const track = isDesktop ? 'desktop' : 'mobile';

  const toneIcons = TONE_ICONS[tone] || TONE_ICONS.neutral;
  const discClass = cn(
    `${prefix}-statusshell__disc`,
    tone !== 'neutral' && `${prefix}-statusshell__disc--${tone}`
  );

  return (
    <div className={cn(`${prefix}-statusshell`, className)}>
      <SkipLink track={track} />

      {showWordmark && (
        <div className={`${prefix}-statusshell__masthead`}>
          <Wordmark className={`${prefix}-statusshell__wordmark`} />
        </div>
      )}

      <div className={`${prefix}-statusshell__col`}>
        <main id={MAIN_ID} className={`${prefix}-statusshell__inner`}>
          <div className={discClass} aria-hidden="true">
            {isDesktop ? (
              <Icon as={icon || toneIcons.desktop} />
            ) : (
              <Glyph name={toneIcons.mobile} />
            )}
          </div>

          {kicker && <p className={`${prefix}-statusshell__kicker`}>{kicker}</p>}
          {title && <h1 className={`${prefix}-statusshell__title`}>{title}</h1>}
          {description && <p className={`${prefix}-statusshell__text`}>{description}</p>}

          {children && <div className={`${prefix}-statusshell__body`}>{children}</div>}
          {actions && <div className={`${prefix}-statusshell__actions`}>{actions}</div>}
        </main>
      </div>

      {showThemeToggle && <ThemeToggle track={track} variant="floating" />}
    </div>
  );
}

export default StatusShell;
